import { useCallback, useState } from "react";

import PageLoading from "components/loading/PageLoading";
import FullScreenModal from "ds/components/FullScreenModal";
import useTitle from "hooks/useTitle";
import useErrorHandle from "hooks/useErrorHandle";
import { showSimpleLeaveConfirmation } from "ds/components/LeaveConfirmationModal/Simple";
import useTypedFlags from "hooks/useTypedFlags";

import useCloseFullScreenModal from "../../useCloseFullScreenModal";
import useGetBitbucketCloudIntegration from "./useGetBitbucketCloudIntegration";
import VCSIntegrationsManageBitbucketCloudForm from "../Form";
import VCSIntegrationsManageBitbucketCloudFormV2 from "../FormNew";

type VCSIntegrationsManageBitbucketCloudEditProps = {
  id: string;
};

const VCSIntegrationsManageBitbucketCloudEdit = ({
  id,
}: VCSIntegrationsManageBitbucketCloudEditProps) => {
  const { bitbucketApiTokenSupport } = useTypedFlags();
  const [isDirty, setIsDirty] = useState(false);

  const { integration, isPageLoading, error } = useGetBitbucketCloudIntegration(id);
  const { handleOnClose } = useCloseFullScreenModal();

  const modalCloseClickHandler = useCallback(async () => {
    if (isDirty) {
      await showSimpleLeaveConfirmation();
    }

    handleOnClose();
  }, [handleOnClose, isDirty]);

  const toggleIsDirty = useCallback((value: boolean) => {
    setIsDirty(value);
  }, []);

  useTitle(`Edit integration · ${integration?.name}`);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  return (
    <FullScreenModal title="Edit Bitbucket Cloud integration" onClose={modalCloseClickHandler}>
      {isPageLoading && <PageLoading />}
      {!isPageLoading && (
        <>
          {bitbucketApiTokenSupport ? (
            <VCSIntegrationsManageBitbucketCloudFormV2
              integration={integration}
              onIsDirtyChange={toggleIsDirty}
            />
          ) : (
            <VCSIntegrationsManageBitbucketCloudForm
              integration={integration}
              onIsDirtyChange={toggleIsDirty}
            />
          )}
        </>
      )}
    </FullScreenModal>
  );
};

export default VCSIntegrationsManageBitbucketCloudEdit;
